import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Color, Grid, Type } from '../app/StyleGuide';
import { MediumOutlineButton } from '../components/Buttons';
import { useUser } from '../features/authentication';
import alertService from '../services/AlertService';
import trainingInstanceService from '../services/trainingInstanceService';
import { handleError } from '../utils/apiUtils';
import { stopProp } from '../utils/domUtils';
import ReportRow from './ReportRow';
import eventBus from '../services/globalEventBus';

const ReportsContainer = styled.div`
  max-width: 720px;
  margin: ${Grid._6} auto 0 auto;

  legend {
    margin-bottom: ${Grid._2};
  }

  h4,
  button {
    margin: 0;
  }

  ul {
    padding: 0;
  }

  li {
    justify-content: space-between;
  }
`;

const ReportsHeader = styled.fieldset`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${Type.Color.dark};
  font-size: ${Type.Scale._3};
  font-weight: ${Type.Weight.semibold};
  border-bottom: 2px solid ${Color.Blue._15};
  padding: ${Grid._3} 0;
`;

const Reports = ({ reports, tasklistName, assigneeName, tasklistInstanceId }) => {
  const { user } = useUser();
  const [data, setData] = useState({ readyReports: reports, isGenerating: false });
  const { readyReports, isGenerating } = data;
  const downloadableReports = readyReports.filter(report => !!report?.reportId);

  const setIsGenerating = value => setData(prev => ({ ...prev, isGenerating: value }));

  const isMountedRef = useRef(true);
  const unSubscribeRef = useRef(null);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (unSubscribeRef.current) {
        unSubscribeRef.current();
      }
    };
  }, []);

  const generateReport = () => {
    if (isMountedRef.current) {
      setIsGenerating(true);
      const newReports = [];

      let generatedReports = 0;

      const unsubscribe = eventBus.subscribe('ReportResultToTasklistAdded', response => {
        generatedReports += 1;
        const result = response.reportResult;
        const individualReport = readyReports.find(r => r.id === result.id);
        if (individualReport) {
          individualReport.reportId = result.reportId;
          newReports.push(individualReport);
        }

        if (generatedReports >= readyReports.length) {
          if (isMountedRef.current) {
            alertService.show(`Report${generatedReports > 1 ? 's' : ''} Generated`);
            setData(prev => ({ ...prev, readyReports: newReports }));
            unsubscribe();
            unSubscribeRef.current = null;
            setIsGenerating(false);
          }
        }
      });

      unSubscribeRef.current = unsubscribe;

      trainingInstanceService.generateReport(tasklistInstanceId, user.userId).catch(error => {
        unsubscribe();
        unSubscribeRef.current = null;
        handleError(error);
        if (isMountedRef.current) setIsGenerating(false);
      });
    }
  };

  return (
    <ReportsContainer>
      <ReportsHeader>
        Reports
        <MediumOutlineButton
          onClick={stopProp(generateReport)}
          loadingIndicatorColor="{Color.Gray._80}"
          operating={isGenerating}
          disabled={isGenerating}
        >
          Regenerate
        </MediumOutlineButton>
      </ReportsHeader>
      <ul>
        {downloadableReports.length > 0 ? (
          downloadableReports.map(report => (
            <ReportRow
              key={report.reportId}
              report={report}
              tasklistName={tasklistName}
              assigneeName={assigneeName}
              tasklistInstanceId={tasklistInstanceId}
            />
          ))
        ) : (
          <p>No related reports were found.</p>
        )}
      </ul>
    </ReportsContainer>
  );
};

Reports.propTypes = {
  reports: PropTypes.array.isRequired,
  tasklistName: PropTypes.string,
  assigneeName: PropTypes.string,
  tasklistInstanceId: PropTypes.string,
};

export default Reports;
